import {Box, Typography} from '@mui/material';
import {GridSortModel} from '@mui/x-data-grid';
import {t} from 'i18next';
import {isEqual, sortBy} from 'lodash';
import React, {Fragment, useCallback, useMemo, useRef, useState} from 'react';
import {IGetCommentsListGridRequestParams} from '../../../../api/types/document.type';
import {ReactComponent as FolderIcon} from '../../../../assets/images/no-data-found.svg';
import {
  useDeleteComment,
  useCommentsListGrid,
} from '../../../../hooks/comments/useCommentsListGrid';
import {PAGE_SIZE, ToastType} from '../../../../shared/constants';
import {ISelectItem} from '../../../../shared/types';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrDataGrid from '../../../common/arr-data-grid/ArrDataGrid';
import AddCommentWrapper from '../add-comment/AddCommentWrapper';
import './CommentsListGrid.scss';
import {getCommentsListGridColumns} from './CommentsListGridColumns';
import ArrModal from '../../../common/arr-modal/ArrModal';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';

interface ICommentsListGridProps {
  selectedGroup: ISelectItem | null;
}

const CommentsListGrid: React.FC<ICommentsListGridProps> = ({
  selectedGroup,
}: ICommentsListGridProps) => {
  const [showAddCommentDialog, setShowAddCommentDialog] =
    useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [showToast, setShowToast] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState(ToastType.Success);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'commentLabel',
      sort: 'asc',
    },
  ]);
  const previousSelectedGroupRef = useRef<{
    selectedGroup: ISelectItem | null;
    sortModel: GridSortModel;
  }>({
    selectedGroup: null,
    sortModel,
  });
  const isOnlyPageChangeRef = useRef<boolean>(false);

  const isResetPageNumber = useCallback(() => {
    let resetPageNumber = false;
    if (
      previousSelectedGroupRef.current?.selectedGroup?.id !== selectedGroup?.id
    ) {
      previousSelectedGroupRef.current = {
        ...previousSelectedGroupRef.current,
        selectedGroup,
      };
      resetPageNumber = true;
    } else if (
      !isEqual(
        sortBy(previousSelectedGroupRef.current?.sortModel, ['field', 'sort']),
        sortBy(sortModel, ['field', 'sort'])
      )
    ) {
      previousSelectedGroupRef.current = {
        ...previousSelectedGroupRef.current,
        sortModel,
      };
      resetPageNumber = true;
    }
    return resetPageNumber;
  }, [selectedGroup, sortModel]);

  const params = useMemo(() => {
    const getPageNumber = isOnlyPageChangeRef.current ? pageNumber : 1;
    const payload: IGetCommentsListGridRequestParams = {
      SortBy: sortModel[0]?.field || 'commentLabel',
      SortingOrder: sortModel[0]?.sort || 'asc',
      PageNumber: getPageNumber,
      PageSize: pageSize,
    };

    if (selectedGroup?.id) {
      payload.SecurityGroupId = Number.parseInt(selectedGroup?.id as string);
    }

    if (isResetPageNumber() && !isOnlyPageChangeRef.current)
      setTimeout(() => setPageNumber(1), 100);

    if (isOnlyPageChangeRef.current) isOnlyPageChangeRef.current = false;
    return payload;
  }, [isResetPageNumber, pageNumber, pageSize, selectedGroup?.id, sortModel]);

  const {
    data: commentsListGridResponse,
    isLoading,
    refetch: refetchCommentsListGrid,
  } = useCommentsListGrid(params);

  const {mutate: deleteComment, isPending} = useDeleteComment();

  const commentsListGridData = useMemo(
    () => commentsListGridResponse?.data ?? [],
    [commentsListGridResponse?.data]
  );

  const totalRecords = useMemo(
    () => commentsListGridResponse?.totalRecords,
    [commentsListGridResponse?.totalRecords]
  );

  const isNoData = useMemo(
    () => commentsListGridData?.length <= 0,
    [commentsListGridData?.length]
  );

  const handleDelete = (params: any) => {
    setSelectedId(params.id);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    deleteComment(selectedId, {
      onSuccess: () => {
        triggerToast(
          ToastType.Success,
          t('manageCommentFields.deleteCommentModal.success')
        );
        if (typeof refetchCommentsListGrid === 'function')
          refetchCommentsListGrid();
      },
      onError: (error) => {
        triggerToast(ToastType.Error, error.message);
      },
    });
  };

  const triggerToast = (
    toastType: ToastType | ((prevState: ToastType) => ToastType),
    toastMessage: string
  ) => {
    setShowToast(true);
    setShowDeleteModal(false);
    setToastType(toastType);
    setToastMessage(toastMessage);
  };

  const handlePageNumber = useCallback((pageNumber: number) => {
    isOnlyPageChangeRef.current = true;
    setPageNumber(pageNumber);
  }, []);

  const handleAddComment = useCallback(() => setShowAddCommentDialog(true), []);

  const onAddCommentSuccess = useCallback(() => {
    if (pageNumber === 1) {
      refetchCommentsListGrid();
    } else {
      setPageNumber(1);
    }
  }, [pageNumber, refetchCommentsListGrid]);

  const NoRowsOverlay = useCallback(
    () => (
      <Box className="no-grid-overlay">
        <FolderIcon />
        <Typography
          tabIndex={0}
          aria-label={t('manageCommentFields.messageForNoGridData')}
          className="typo-text headline3_size"
          data-testid={`comments-list-grid-message-for-no-grid-data`}>
          {t('manageCommentFields.messageForNoGridData')}
        </Typography>
      </Box>
    ),
    []
  );

  return (
    <Fragment>
      <div data-testid={'comments-list-grid-table'}>
        <Box className={'subheader-container'}>
          <ArrButton
            dataTestId={'add-comment-field'}
            variant={'primary'}
            size={'medium'}
            onClick={handleAddComment}
          >
            {t('manageCommentFields.addCommentField')}
          </ArrButton>
        </Box>
        <ArrDataGrid
          className={
            isNoData
              ? 'comments-list-grid no-grid-overlay-height'
              : 'comments-list-grid'
          }
          loading={isLoading}
          columns={getCommentsListGridColumns({handleDelete})}
          rows={commentsListGridData}
          primaryKey={'commentId'}
          noRowsOverlay={NoRowsOverlay}
          hideFooter={isNoData}
          rowCount={totalRecords}
          currentPage={pageNumber}
          itemsPerPage={PAGE_SIZE}
          showPerPageOptions
          pageSizeOptions={[PAGE_SIZE]}
          paginationMode={'server'}
          onPageChange={handlePageNumber}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
        />
      </div>
      <AddCommentWrapper
        securityGroupId={Number(selectedGroup?.id ?? 0)}
        showModal={showAddCommentDialog}
        onClose={setShowAddCommentDialog}
        onAddComment={onAddCommentSuccess}
      />
      <ArrModal
        showModal={showDeleteModal}
        ariaLabel={t('manageCommentFields.deleteCommentModal.title')}
        title={t('manageCommentFields.deleteCommentModal.title')}
        ariaDescription={t(
          'manageCommentFields.deleteCommentModal.description'
        )}
        description={t('manageCommentFields.deleteCommentModal.description')}
        onClose={() => setShowDeleteModal(false)}
        autoFocusCloseButton={true}
        dataTestId="commentslist-modal"
        buttons={[
          {
            id: 'cancel-button',
            label: t('common.cancel'),
            dataTestId: 'cl-cancel-button',
            onClick: () => setShowDeleteModal(false),
            variant: 'secondary',
            disabled: isPending,
          },
          {
            id: 'delete-button',
            label: t('common.delete'),
            dataTestId: 'cl-delete-button',
            onClick: confirmDelete,
            variant: 'primary',
            loading: isPending,
            disabled: isPending,
          },
        ]}
      />
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => setShowToast(false)}
          status={[toastType]}
        />
      )}
    </Fragment>
  );
};

export {CommentsListGrid};
